import { useEffect, useRef, useState } from "react";
import { io } from "socket.io-client";
import { api, SOCKET_URL, SOCKET_PATH } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Send, MessageCircle } from "lucide-react";

const AdminChat = () => {
  const { token } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const socketRef = useRef(null);
  const scrollRef = useRef(null);

  const loadSessions = () =>
    api.get("/chat/sessions").then((r) => setSessions(r.data)).catch(() => {});

  useEffect(() => {
    loadSessions();
  }, []);

  // Single global socket — joins admin room
  useEffect(() => {
    if (!token) return;
    socketRef.current = io(SOCKET_URL, { path: SOCKET_PATH, transports: ["websocket", "polling"] });
    socketRef.current.on("connect", () => {
      socketRef.current.emit("join_admin", { token });
    });
    socketRef.current.on("session_created", () => loadSessions());
    socketRef.current.on("new_message", (msg) => {
      // Update sessions list
      loadSessions();
      // If active session is the one with the new message, append it
      setMessages((prev) => {
        if (msg.session_id !== active?.id) return prev;
        if (prev.find((m) => m.id === msg.id)) return prev;
        return [...prev, msg];
      });
    });
    return () => {
      if (socketRef.current?.connected) socketRef.current.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, active?.id]);

  const openSession = async (s) => {
    setActive(s);
    const res = await api.get(`/chat/sessions/${s.id}/messages`);
    setMessages(res.data);
    if (socketRef.current?.connected) {
      socketRef.current.emit("join_room", { room_id: s.id });
    }
  };

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = (e) => {
    e?.preventDefault();
    const t = text.trim();
    if (!t || !active || !socketRef.current?.connected) return;
    socketRef.current.emit("send_message", {
      session_id: active.id,
      sender: "admin",
      sender_name: "Township",
      text: t,
    });
    setText("");
  };

  return (
    <div data-testid="admin-chat-page" className="-m-8 h-[calc(100vh-0px)] grid grid-cols-[20rem_1fr]">
      <aside className="bg-white border-r border-border overflow-y-auto" data-testid="admin-chat-sessions">
        <div className="p-5 border-b border-border">
          <h1 className="font-heading text-xl font-semibold">Live Chat Inbox</h1>
          <p className="text-xs text-muted-foreground mt-1">Visitor conversations from the site</p>
        </div>
        {sessions.length === 0 && (
          <div className="p-6 text-sm text-muted-foreground">No conversations yet.</div>
        )}
        <ul className="divide-y divide-border">
          {sessions.map((s) => (
            <li key={s.id}>
              <button
                onClick={() => openSession(s)}
                data-testid={`admin-chat-session-${s.id}`}
                className={`w-full text-left p-4 hover:bg-muted/40 transition-colors ${active?.id === s.id ? "bg-muted/60" : ""}`}
              >
                <div className="flex items-center justify-between">
                  <div className="font-medium">{s.visitor_name}</div>
                  <div className="text-[10px] text-muted-foreground">{new Date(s.last_message_at).toLocaleString()}</div>
                </div>
                <div className="text-xs text-muted-foreground truncate mt-1">{s.last_preview || "No messages yet"}</div>
              </button>
            </li>
          ))}
        </ul>
      </aside>

      <section className="flex flex-col bg-muted/20">
        {!active ? (
          <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground gap-3">
            <MessageCircle size={32} className="opacity-50" />
            <div>Select a conversation to view messages.</div>
          </div>
        ) : (
          <>
            <div className="p-4 border-b border-border bg-white">
              <div className="font-heading text-lg font-semibold">{active.visitor_name}</div>
              <div className="text-xs text-muted-foreground">Started {new Date(active.created_at).toLocaleString()}</div>
            </div>
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-3" data-testid="admin-chat-thread">
              {messages.map((m) => {
                const isAdmin = m.sender === "admin";
                return (
                  <div key={m.id} className={`flex ${isAdmin ? "justify-end" : "justify-start"}`} data-testid={`admin-chat-msg-${m.sender}`}>
                    <div className={`max-w-[70%] rounded-2xl px-4 py-2 text-sm shadow-sm ${
                      isAdmin ? "bg-primary text-primary-foreground rounded-br-sm" : "bg-white border border-border rounded-bl-sm"
                    }`}>
                      <div className={`text-[10px] uppercase tracking-wider mb-1 ${isAdmin ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
                        {m.sender_name}
                      </div>
                      <div>{m.text}</div>
                    </div>
                  </div>
                );
              })}
            </div>
            <form onSubmit={send} className="p-3 border-t border-border bg-white flex gap-2">
              <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Type a reply..." data-testid="admin-chat-input" />
              <Button type="submit" className="bg-primary hover:bg-primary/90" data-testid="admin-chat-send-btn">
                <Send size={16} className="mr-1.5" /> Send
              </Button>
            </form>
          </>
        )}
      </section>
    </div>
  );
};

export default AdminChat;
